import { useMemo, useRef, type CSSProperties } from "react";
import { motion, useInView, useReducedMotion, type UseInViewOptions } from "motion/react";
import { cn } from "@/components/ui/utils";

export interface ShimmeringTextProps {
  /** Text to display with shimmer effect */
  text: string;
  /** Animation duration in seconds */
  duration?: number;
  /** Delay before starting animation */
  delay?: number;
  /** Whether to repeat the animation */
  repeat?: boolean;
  /** Pause duration between repeats in seconds */
  repeatDelay?: number;
  /** Custom className */
  className?: string;
  /** Whether to start animation when component enters viewport */
  startOnView?: boolean;
  /** Whether to animate only once */
  once?: boolean;
  /** Margin for in-view detection (rootMargin) */
  inViewMargin?: UseInViewOptions["margin"];
  /** Shimmer spread multiplier */
  spread?: number;
  /** Base text color */
  color?: string;
  /** Shimmer gradient color */
  shimmerColor?: string;
}

export function ShimmeringText({
  text,
  duration = 2,
  delay = 0,
  repeat = true,
  repeatDelay = 0.5,
  className,
  startOnView = true,
  once = false,
  inViewMargin,
  spread = 2,
  color,
  shimmerColor,
}: ShimmeringTextProps) {
  const ref = useRef<HTMLSpanElement>(null);
  const isInView = useInView(ref, { once, margin: inViewMargin });
  const prefersReducedMotion = useReducedMotion();

  // Calculate dynamic spread based on text length
  const dynamicSpread = useMemo(() => {
    return text.length * spread;
  }, [text, spread]);

  // Determine if we should start animation
  const shouldAnimate = !startOnView || isInView;

  return (
    <motion.span
      ref={ref}
      className={cn(
        "relative inline-block bg-[length:250%_100%,auto] bg-clip-text text-transparent",
        "[--base-color:var(--muted-foreground)] [--shimmer-color:var(--foreground)]",
        "[background-repeat:no-repeat,padding-box]",
        "[--shimmer-bg:linear-gradient(90deg,transparent_calc(50%-var(--spread)),var(--shimmer-color),transparent_calc(50%+var(--spread)))]",
        "dark:[--base-color:var(--muted-foreground)] dark:[--shimmer-color:var(--foreground)]",
        className,
      )}
      style={
        {
          "--spread": `${dynamicSpread}px`,
          ...(color ? { "--base-color": color } : {}),
          ...(shimmerColor ? { "--shimmer-color": shimmerColor } : {}),
          backgroundImage: prefersReducedMotion
            ? undefined
            : `var(--shimmer-bg), linear-gradient(var(--base-color), var(--base-color))`,
          color: prefersReducedMotion ? color || "inherit" : undefined,
        } as CSSProperties
      }
      initial={{
        backgroundPosition: "100% center",
        opacity: prefersReducedMotion ? 1 : 0,
      }}
      animate={
        shouldAnimate
          ? {
              backgroundPosition: prefersReducedMotion ? "50% center" : "0% center",
              opacity: 1,
            }
          : {}
      }
      transition={{
        backgroundPosition: {
          repeat: repeat && !prefersReducedMotion ? Infinity : 0,
          duration,
          delay,
          repeatDelay,
          ease: "linear",
        },
        opacity: {
          duration: prefersReducedMotion ? 0 : 0.3,
          delay,
        },
      }}
    >
      {text}
    </motion.span>
  );
}
